<script setup lang="ts">
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { computed, h, type VNode } from 'vue';

import type { ProposalState } from '@/composables/useCopilotConversation';
import ActionChip from './ActionChip.vue';
import { CHIP_SENTINEL_SPLIT, extractAnchors } from './anchor-extractor';

/**
 * Per ADR-0052. Renders an assistant turn as a constrained markdown
 * pass with one custom inline primitive: an action anchor of the form
 *
 *   [Label text](#proposal-<proposalId>)
 *
 * which the renderer extracts and replaces with an `<ActionChip>` Vue
 * component bound to the matching `ProposalState` from this message's
 * own proposals array (not a global store, so re-rendering a historical
 * message picks up that message's own proposals).
 *
 * The HTML between anchors is sanitised by DOMPurify with a closed
 * allow-list matching the TestChatPanel agent renderer (bold, italic,
 * lists, code, plain links). Plain `<a>` nodes that aren't proposal
 * anchors render as regular links via the inner HTML; only proposal
 * anchors get the chip upgrade.
 *
 * Streaming-safe: while a turn streams in, partial markdown (an
 * unclosed bold, a half-finished anchor) renders as text. The chip
 * appears the moment the closing `)` arrives.
 */
const props = defineProps<{
  source: string;
  proposals?: ProposalState[];
}>();

const ALLOWED_TAGS = ['p', 'br', 'strong', 'em', 'code', 'pre', 'ul', 'ol', 'li', 'a'];
const ALLOWED_ATTRS = ['href', 'target', 'rel'];

function renderMarkdownToHtml(source: string): string {
  const html = marked.parse(source, { async: false }) as string;
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR: ALLOWED_ATTRS,
    ALLOWED_URI_REGEXP: /^(?:https?|mailto|tel):|^[/#?]/i,
  });
}

const rendered = computed(() => {
  const { processed, chips } = extractAnchors(props.source);
  const html = renderMarkdownToHtml(processed);
  return { html, chips };
});

function findProposal(proposalId: string): ProposalState | undefined {
  return props.proposals?.find(
    (p) => (p.proposal as { proposalId?: string }).proposalId === proposalId,
  );
}

// Splits the sanitised HTML on chip sentinels and emits a mix of
// inner-HTML spans and `<ActionChip>` components. Using a render
// function (instead of a template with v-for + v-html) is what lets us
// drop a real Vue component into the middle of an HTML stream without
// re-parsing it ourselves.
function renderNodes(): VNode[] {
  const { html, chips } = rendered.value;
  const parts = html.split(CHIP_SENTINEL_SPLIT);
  const nodes: VNode[] = [];
  for (let i = 0; i < parts.length; i++) {
    const segment = parts[i] ?? '';
    if (i % 2 === 0) {
      if (segment.length > 0) {
        nodes.push(
          h('span', {
            class: 'assistant-md',
            innerHTML: segment,
          }),
        );
      }
    } else {
      const idx = Number.parseInt(segment, 10);
      const placeholder = chips[idx];
      if (!placeholder) continue;
      const state = findProposal(placeholder.proposalId);
      nodes.push(
        h(ActionChip, {
          proposalId: placeholder.proposalId,
          label: placeholder.label,
          state: state ?? null,
        }),
      );
    }
  }
  return nodes;
}
</script>

<template>
  <div class="assistant-markdown">
    <component :is="{ render: renderNodes }" />
  </div>
</template>

<style scoped>
.assistant-markdown :deep(p) {
  margin: 0;
}

.assistant-markdown :deep(p + p) {
  margin-top: 0.5em;
}

.assistant-markdown :deep(ul),
.assistant-markdown :deep(ol) {
  margin: 0.25em 0;
  padding-left: 1.25em;
}

.assistant-markdown :deep(li + li) {
  margin-top: 0.15em;
}

.assistant-markdown :deep(code) {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.9em;
  padding: 0.05em 0.3em;
  border-radius: 4px;
  background-color: var(--color-bg, rgba(0, 0, 0, 0.05));
}

.assistant-markdown :deep(a) {
  color: var(--color-accent, #2563eb);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.assistant-markdown :deep(strong) {
  font-weight: 600;
}
</style>
